/* __next_internal_client_entry_do_not_use__ PhoneInput auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { getIsoToCountryNames } from "@faire/web--source/common/consts/ISO_TO_COUNTRY_NAME";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { countryCallingCodeOptions, parsePhoneNumber } from "@faire/web--source/common/phoneNumber";
import { Chevron } from "@faire/web--source/slate/icons/foundation/Chevron";
import { FormInputLabel, inputBorderStyles, inputStylesNoBorder } from "@faire/web--source/slate/Input";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import ClickOutside from "@faire/web--source/ui/ClickOutside";
import { ReadOnlyText } from "@faire/web--source/ui/Scaffolding/ReadOnlyText";
import omit from "lodash/omit";
// eslint-disable-next-line @faire-react/no-restricted-imports
import uniqueId from "lodash/uniqueId";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NumberFormatBase, usePatternFormat } from "react-number-format";
import styled, { useTheme } from "styled-components";
import { getDerivedPropsFromErrorProps } from "../error";
var CountryCodeButton = styled.button.withConfig({
    componentId: "sc-d1a234a5-0"
})([
    "display:flex;align-items:center;justify-content:center;padding:0 10px;border:none;appearance:none;outline:none;flex-basis:64px;border-right:1px solid ",
    ";background-color:",
    ";&:disabled{background-color:",
    ";cursor:default;}&:focus-visible{outline:2px solid -webkit-focus-ring-color;z-index:1;}"
], Core.border.muted, Core.surface.inverse, Core.surface.muted);
var Border = styled(Row).withConfig({
    componentId: "sc-d1a234a5-1"
})([
    "",
    ";min-height:42px;&:focus,&:focus-within{",
    "{border-right:1px solid ",
    ";}}"
], inputBorderStyles, CountryCodeButton, Core.border.subdued);
var CountryCodeDropdownWrapper = styled.div.withConfig({
    componentId: "sc-d1a234a5-2"
})([
    "position:relative;"
]);
var NumberFormat = styled(NumberFormatBase).withConfig({
    componentId: "sc-d1a234a5-3"
})([
    "",
    ";flex:1;"
], inputStylesNoBorder);
var CountryCodeDropdown = styled.ul.withConfig({
    componentId: "sc-d1a234a5-4"
})([
    "height:230px;position:absolute;left:0;top:0;border:1px solid ",
    ";border-top:0;overflow-y:auto;background:",
    ";box-sizing:border-box;width:100%;margin:0;padding:0;z-index:1;"
], Core.border.muted, Core.surface.inverse);
var CountryCallingCodeOptionButton = styled.button.withConfig({
    componentId: "sc-d1a234a5-5"
})([
    "display:flex;align-items:center;justify-content:center;text-align:left;width:100%;padding:10px 0;cursor:pointer;background:",
    ";&:hover{background:",
    ";}"
], function(props) {
    return props.$highlight ? Core.surface.subdued : "none";
}, Core.surface.subdued);
var CountryCallingCodeOptionLabel = styled(Typography).withConfig({
    componentId: "sc-d1a234a5-6"
})([
    "flex:1;"
]);
var optionDataId = function(option) {
    return "".concat(option.label).concat(option.countryCallingCode);
};
// eslint-disable-next-line complexity
var PhoneInputBase = function(props) {
    var _parsedDefaultPhoneNumber_current, _theme_input_label, _countryCodeOptions_, _theme_input_helper;
    var theme = useTheme();
    var strictLocalize = useStrictLocalization().strictLocalize;
    var onChangePhoneNumber = props.onChangePhoneNumber, defaultPhoneNumber = props.defaultPhoneNumber, defaultCountry = props.defaultCountry;
    var _props_id;
    var id = (_props_id = props.id) !== null && _props_id !== void 0 ? _props_id : uniqueId("phone-input-");
    var _props_countryCodeOptions;
    var countryCodeOptions = (_props_countryCodeOptions = props.countryCodeOptions) !== null && _props_countryCodeOptions !== void 0 ? _props_countryCodeOptions : countryCallingCodeOptions(strictLocalize);
    var getCountryCallOptionIndexByLabel = useCallback(function(label) {
        var index = countryCodeOptions.findIndex(function(option) {
            return option.label === label;
        });
        return Math.max(index, 0);
    }, [
        countryCodeOptions
    ]);
    var isoToCountryNames = useMemo(function() {
        return getIsoToCountryNames(strictLocalize);
    }, [
        strictLocalize
    ]);
    var getCountryCallOptionIndexByISO = useCallback(function(countryIso) {
        if (!countryIso) {
            return 0;
        }
        var _isoToCountryNames_countryIso;
        return getCountryCallOptionIndexByLabel((_isoToCountryNames_countryIso = isoToCountryNames[countryIso]) !== null && _isoToCountryNames_countryIso !== void 0 ? _isoToCountryNames_countryIso : "");
    }, [
        getCountryCallOptionIndexByLabel,
        strictLocalize,
        isoToCountryNames
    ]);
    var _useState = _sliced_to_array(useState(defaultCountry ? getCountryCallOptionIndexByISO(defaultCountry) : undefined), 2), selectedOptionIndex = _useState[0], setSelectedOptionIndex = _useState[1];
    var defaultCountryRef = useRef(defaultCountry);
    useEffect(function() {
        if (defaultCountry !== defaultCountryRef.current) {
            setSelectedOptionIndex(getCountryCallOptionIndexByISO(defaultCountry));
            defaultCountryRef.current = defaultCountry;
        }
    }, [
        defaultCountry,
        getCountryCallOptionIndexByISO
    ]);
    var selectedOption = countryCodeOptions[selectedOptionIndex !== null && selectedOptionIndex !== void 0 ? selectedOptionIndex : 0];
    var parsedDefaultPhoneNumber = useRef(defaultPhoneNumber ? parsePhoneNumber(defaultPhoneNumber, selectedOption, strictLocalize) : undefined);
    var _parsedDefaultPhoneNumber_current_nationalPhoneNumber;
    var _useState1 = _sliced_to_array(useState((_parsedDefaultPhoneNumber_current_nationalPhoneNumber = (_parsedDefaultPhoneNumber_current = parsedDefaultPhoneNumber.current) === null || _parsedDefaultPhoneNumber_current === void 0 ? void 0 : _parsedDefaultPhoneNumber_current.nationalPhoneNumber) !== null && _parsedDefaultPhoneNumber_current_nationalPhoneNumber !== void 0 ? _parsedDefaultPhoneNumber_current_nationalPhoneNumber : ""), 2), phoneNumber = _useState1[0], setPhoneNumber = _useState1[1];
    // This useEffect is used to set the default country code when the component is first rendered.
    // It will also send a onChangePhoneNumber when there is a defaultPhoneNumber
    useEffect(function() {
        if (parsedDefaultPhoneNumber.current) {
            var countryCallOption = getCountryCallOptionIndexByLabel(parsedDefaultPhoneNumber.current.countryCallingCodeOption.label);
            setSelectedOptionIndex(countryCallOption);
            onChangePhoneNumber === null || onChangePhoneNumber === void 0 ? void 0 : onChangePhoneNumber("".concat(parsedDefaultPhoneNumber.current.countryCallingCodeOption.countryCallingCode, " ").concat(parsedDefaultPhoneNumber.current.nationalPhoneNumber));
            parsedDefaultPhoneNumber.current = undefined;
        }
    }, [
        getCountryCallOptionIndexByLabel,
        onChangePhoneNumber
    ]);
    var pastedNationalPhoneNumber = useRef("");
    var pastedCountryCallingCodeOption = useRef({
        label: "United States of America",
        countryCallingCode: "+1",
        format: "(###)-###-####"
    });
    var _useState2 = _sliced_to_array(useState(false), 2), showingDropdown = _useState2[0], setShowingDropdown = _useState2[1];
    var inputContainerRef = useRef(null);
    var dropdownRef = useRef(null);
    var triggerOnChange = function(params) {
        var _params_updatedPhoneNumber;
        var phoneNumberWithCode = (_params_updatedPhoneNumber = params.updatedPhoneNumber) !== null && _params_updatedPhoneNumber !== void 0 ? _params_updatedPhoneNumber : phoneNumber;
        if (params.updatedCountryCallingCode) {
            phoneNumberWithCode = "".concat(params.updatedCountryCallingCode, " ").concat(phoneNumberWithCode);
        } else if (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.countryCallingCode) {
            phoneNumberWithCode = "".concat(selectedOption.countryCallingCode, " ").concat(phoneNumberWithCode);
        }
        onChangePhoneNumber === null || onChangePhoneNumber === void 0 ? void 0 : onChangePhoneNumber(phoneNumberWithCode);
    };
    var handleChange = function(numberFormatValues) {
        if (pastedNationalPhoneNumber.current !== "") {
            setSelectedOptionIndex(getCountryCallOptionIndexByLabel(pastedCountryCallingCodeOption.current.label));
            setPhoneNumber(pastedNationalPhoneNumber.current);
            triggerOnChange({
                updatedCountryCallingCode: pastedCountryCallingCodeOption.current.countryCallingCode,
                updatedPhoneNumber: pastedNationalPhoneNumber.current
            });
            pastedNationalPhoneNumber.current = "";
        } else {
            setPhoneNumber(numberFormatValues.value);
            triggerOnChange({
                updatedPhoneNumber: numberFormatValues.value
            });
        }
    };
    var handleClickOutside = function() {
        setShowingDropdown(false);
    };
    var focusInputElement = function() {
        if (!inputContainerRef.current) {
            return;
        }
        var inputEl = inputContainerRef.current.querySelector("input");
        if (inputEl) {
            inputEl.focus();
        }
    };
    var ensureSelectedOptionIsVisible = function() {
        var // scrollIntoView is undefined in unit tests
        _optionEl_scrollIntoView;
        if (!dropdownRef.current || selectedOption === undefined) {
            return;
        }
        var optionEl = dropdownRef.current.querySelector('[data-id="'.concat(optionDataId(selectedOption), '"]'));
        optionEl === null || optionEl === void 0 ? void 0 : (_optionEl_scrollIntoView = optionEl.scrollIntoView) === null || _optionEl_scrollIntoView === void 0 ? void 0 : _optionEl_scrollIntoView.call(optionEl, {
            block: "center"
        });
    };
    var selectCountryCode = function(selectedOptionIndex) {
        var _countryCodeOptions_selectedOptionIndex;
        setSelectedOptionIndex(selectedOptionIndex);
        setShowingDropdown(false);
        triggerOnChange({
            updatedCountryCallingCode: (_countryCodeOptions_selectedOptionIndex = countryCodeOptions[selectedOptionIndex]) === null || _countryCodeOptions_selectedOptionIndex === void 0 ? void 0 : _countryCodeOptions_selectedOptionIndex.countryCallingCode
        });
        focusInputElement();
    };
    var handleCountryCallingCodeButtonClicked = function() {
        setShowingDropdown(!showingDropdown);
    };
    var handleKeyDown = function(e) {
        if (selectedOptionIndex === undefined) {
            return;
        }
        switch(e.key){
            case "ArrowUp":
                setSelectedOptionIndex(Math.max(selectedOptionIndex - 1, 0));
                ensureSelectedOptionIsVisible();
                // prevent screen from scrolling up
                e.preventDefault();
                break;
            case "ArrowDown":
                setSelectedOptionIndex(Math.min(selectedOptionIndex + 1, countryCodeOptions.length - 1));
                ensureSelectedOptionIsVisible();
                // prevent screen from scrolling down
                e.preventDefault();
                break;
            case "Enter":
                // prevent the button's onClick from firing
                e.preventDefault();
                setShowingDropdown(false);
                break;
            default:
                setShowingDropdown(false);
        }
    };
    var handlePaste = function(event) {
        var pastedPhoneNumber = event.clipboardData.getData("Text");
        var _parsePhoneNumber = parsePhoneNumber(pastedPhoneNumber, selectedOption, strictLocalize), nationalPhoneNumber = _parsePhoneNumber.nationalPhoneNumber, countryCallingCodeOption = _parsePhoneNumber.countryCallingCodeOption;
        pastedCountryCallingCodeOption.current = countryCallingCodeOption;
        pastedNationalPhoneNumber.current = nationalPhoneNumber;
    };
    var error = props.error, label = props.label, helper = props.helper, format = props.format, ariaLabel = props["aria-label"], rest = _object_without_properties(props, [
        "error",
        "label",
        "helper",
        "format",
        "aria-label"
    ]);
    var _selectedOption_format;
    var phoneNumberFormat = (_selectedOption_format = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.format) !== null && _selectedOption_format !== void 0 ? _selectedOption_format : format;
    var _usePatternFormat = usePatternFormat({
        mask: "_",
        format: phoneNumberFormat !== null && phoneNumberFormat !== void 0 ? phoneNumberFormat : ""
    }), patternFormat = _usePatternFormat.format;
    var _format = function(inputValue) {
        if (phoneNumberFormat && patternFormat) {
            return patternFormat(inputValue);
        }
        return inputValue;
    };
    var inputProps = omit(rest, [
        "onChangePhoneNumber",
        "defaultPhoneNumber",
        "defaultCountry",
        "countryCodeOptions",
        "strictLocalize"
    ]);
    var _getDerivedPropsFromErrorProps = getDerivedPropsFromErrorProps({
        error: error
    }), showErrorState = _getDerivedPropsFromErrorProps.showErrorState, showErrorMessage = _getDerivedPropsFromErrorProps.showErrorMessage, errorMessage = _getDerivedPropsFromErrorProps.errorMessage;
    var inputId = id !== null && id !== void 0 ? id : id;
    var labelId = label ? "".concat(inputId, "__label") : undefined;
    if (props.readonly) {
        var _countryCodeOptions_1;
        var _countryCodeOptions__countryCallingCode;
        return /*#__PURE__*/ _jsx(ReadOnlyText, {
            label: labelId,
            value: (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.countryCallingCode) ? (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.countryCallingCode) + " " + phoneNumber : ((_countryCodeOptions__countryCallingCode = (_countryCodeOptions_1 = countryCodeOptions[0]) === null || _countryCodeOptions_1 === void 0 ? void 0 : _countryCodeOptions_1.countryCallingCode) !== null && _countryCodeOptions__countryCallingCode !== void 0 ? _countryCodeOptions__countryCallingCode : "") + " " + phoneNumber
        });
    }
    var _theme_input_label1, _theme_input_label_color, _props_sideCta, _selectedOption_countryCallingCode, _theme_input_helper_color;
    return /*#__PURE__*/ _jsxs(ClickOutside, {
        onClickOutside: handleClickOutside,
        onKeyDown: handleKeyDown,
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                justify: "space-between",
                children: [
                    /*#__PURE__*/ _jsx(FormInputLabel, {
                        id: labelId,
                        $typography: _object_spread_props(_object_spread({}, (_theme_input_label1 = theme.input.label) !== null && _theme_input_label1 !== void 0 ? _theme_input_label1 : {}), {
                            color: showErrorState ? Core.text.critical : (_theme_input_label_color = (_theme_input_label = theme.input.label) === null || _theme_input_label === void 0 ? void 0 : _theme_input_label.color) !== null && _theme_input_label_color !== void 0 ? _theme_input_label_color : "faireBlack"
                        }),
                        htmlFor: inputId,
                        children: label
                    }),
                    (_props_sideCta = props.sideCta) !== null && _props_sideCta !== void 0 ? _props_sideCta : null
                ]
            }),
            /*#__PURE__*/ _jsxs(Border, {
                $showErrorState: showErrorState,
                ref: inputContainerRef,
                children: [
                    /*#__PURE__*/ _jsxs(CountryCodeButton, {
                        type: "button",
                        onClick: handleCountryCallingCodeButtonClicked,
                        "data-test-id": "countryCallingCodeButton",
                        disabled: rest.disabled,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraphSansRegular",
                                truncate: true,
                                color: rest.disabled ? Core.text.disabled : Core.text.default,
                                children: (_selectedOption_countryCallingCode = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.countryCallingCode) !== null && _selectedOption_countryCallingCode !== void 0 ? _selectedOption_countryCallingCode : (_countryCodeOptions_ = countryCodeOptions[0]) === null || _countryCodeOptions_ === void 0 ? void 0 : _countryCodeOptions_.countryCallingCode
                            }),
                            /*#__PURE__*/ _jsx(Spacer, {
                                size: 4
                            }),
                            /*#__PURE__*/ _jsx(Chevron, {
                                direction: "down",
                                color: Core.icon.default,
                                fontSize: "8px",
                                strokeWidth: 2
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(NumberFormat, _object_spread(_object_spread_props(_object_spread({
                        id: inputId,
                        type: "tel",
                        autoComplete: "tel"
                    }, inputProps), {
                        value: phoneNumber,
                        format: _format,
                        onPaste: handlePaste,
                        onValueChange: handleChange
                    }), ariaLabel ? {
                        "aria-label": ariaLabel
                    } : undefined, !ariaLabel && labelId ? {
                        "aria-labelledby": labelId
                    } : undefined))
                ]
            }),
            /*#__PURE__*/ _jsx(CountryCodeDropdownWrapper, {
                ref: dropdownRef,
                children: !props.disabled && showingDropdown && /*#__PURE__*/ _jsx(CountryCodeDropdown, {
                    "data-test-id": "countryCallingCodeDropdown",
                    children: countryCodeOptions.map(function(option, index) {
                        return /*#__PURE__*/ _jsx("li", {
                            children: /*#__PURE__*/ _jsxs(CountryCallingCodeOptionButton, {
                                type: "button",
                                tabIndex: -1,
                                onClick: function() {
                                    return selectCountryCode(index);
                                },
                                $highlight: option === selectedOption,
                                "data-id": optionDataId(option),
                                "data-test-id": "countryCallingCode".concat(option.countryCallingCode),
                                children: [
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: 12
                                    }),
                                    /*#__PURE__*/ _jsx(CountryCallingCodeOptionLabel, {
                                        children: option.label
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: 12
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraphSansRegular",
                                        color: Core.text.default,
                                        children: option.countryCallingCode
                                    }),
                                    /*#__PURE__*/ _jsx(Spacer, {
                                        size: 12
                                    })
                                ]
                            })
                        }, optionDataId(option));
                    })
                })
            }),
            showErrorMessage ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: 4
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "labelSansRegular",
                        color: Core.text.critical,
                        children: errorMessage
                    })
                ]
            }) : null,
            !showErrorMessage && helper ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: 4
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "labelSansRegular",
                        color: (_theme_input_helper_color = (_theme_input_helper = theme.input.helper) === null || _theme_input_helper === void 0 ? void 0 : _theme_input_helper.color) !== null && _theme_input_helper_color !== void 0 ? _theme_input_helper_color : Core.text.subdued,
                        children: helper
                    })
                ]
            }) : null
        ]
    });
};
/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/component/input/web/phone-input)
 */ export var PhoneInput = observer(PhoneInputBase);
